import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';

import { Orders, Form } from './components';
import Container from 'components/Container';
import Main from 'layouts/Main';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import useSendEmail from 'Hooks/useSendEmail';
import { useNotify } from 'components/NotificationProvider';

import { getBasketFromLocalStorage } from '@akyga/shared-ui';

import { useNavigate } from 'react-router-dom';

const Basket = () => {
  const [open, setOpen] = useState(false);
  const [basket, setBasket] = useState(getBasketFromLocalStorage());
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    message: '',
    captchaToken: '',
    website: '',
  });
  const { t } = useTranslation();
  const { sendEmail, isLoading, isSuccess, error } = useSendEmail();
  const { notify, notifyError } = useNotify();
  const navigate = useNavigate();

  useEffect(() => {
    if (isSuccess) {
      setOpen(true);
      setBasket({});
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        message: '',
        captchaToken: '',
        website: '',
      });
      localStorage.removeItem('formData');
      localStorage.removeItem('basket');
      navigate('/quote-success');
    }
  }, [isSuccess, navigate]);

  useEffect(() => {
    if (isSuccess === false && error) {
      notifyError(error);
    }
  }, [isSuccess, error, notifyError]);

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleRequestQuote = async () => {
    const formFilled = isFormFilled();
    if (formFilled) {
      await sendEmail(formData, basket);
    } else {
      notify(
        'Please fill out all required fields before requesting a quote.',
        'warning',
      );
    }
  };

  const isFormFilled = () => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.captchaToken.trim() !== '' &&
      formData.website.trim() === ''
    );
  };

  const isBasketEmpty = Object.keys(basket).length === 0;

  return (
    <Main>
      <Helmet>
        <title>{t('contactLanding.title')}</title>
      </Helmet>
      <Container>
        <Box sx={{ padding: 4 }}>
          <Grid container spacing={{ xs: 4, md: 8 }}>
            <Grid item xs={12} md={7}>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={700} marginBottom={4}>
                    {t('basket.contactInformation')}
                  </Typography>
                  <Form
                    formData={formData}
                    setFormData={setFormData}
                    handleSubmit={handleRequestQuote}
                    disabled={isLoading}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="h6" fontWeight={700} marginBottom={4}>
                {t('basket.orderSummary')}
              </Typography>
              <Card variant={'outlined'} sx={{ padding: { xs: 2, sm: 4 } }}>
                <Orders basket={basket} setBasket={setBasket} />
                <Button
                  variant={'contained'}
                  size={'large'}
                  fullWidth
                  onClick={handleRequestQuote}
                  disabled={!isFormFilled() || isBasketEmpty || isLoading}
                  sx={{
                    marginTop: 2,
                    opacity: isBasketEmpty || isLoading ? 0.5 : 1,
                  }}
                >
                  {isLoading ? t('basket.sending') : t('basket.requestQuote')}
                </Button>
                {isLoading && (
                  <Typography
                    variant={'subtitle2'}
                    align={'center'}
                    sx={{ marginTop: 2 }}
                  >
                    {t('basket.sendingMessage')}
                  </Typography>
                )}
                <Box
                  sx={{
                    marginTop: 2,
                    marginRight: { xs: -2, sm: -4 },
                    marginLeft: { xs: -2, sm: -4 },
                    marginBottom: { xs: -2, sm: -4 },
                    padding: { xs: 2, sm: 4 },
                    bgcolor: 'alternate.main',
                  }}
                ></Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
        {/* Notyfikacja */}
        <Snackbar
          open={open}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleClose}
            severity="success"
            sx={{ width: '100%', marginTop: '60px' }}
          >
            Request has been sent!
          </Alert>
        </Snackbar>
      </Container>
    </Main>
  );
};

export default Basket;
